import {
  FETCH_APPLIANCE_METRICS_BY_IDENTIFIER,
  FETCH_APPLIANCE_METRICS_BY_IDENTIFIER_STARTED,
  FETCH_APPLIANCE_METRICS_BY_IDENTIFIER_COMPLETED,
  FETCH_APPLIANCE_LOCATIONS,
  APPLIANCE_DEVICES_FETCH_STARTED,
  APPLIANCE_DEVICES_FETCH_SUCCEEDED,
  ALL_APPLIANCE_DEVICES_FETCH_SUCCEEDED,
  APPLIANCE_DEVICES_FETCH_FAILED,
  APPLIANCE_DEVICES_CLEAR,
  ALL_APPLIANCE_DEVICES_SOCKET_UPDATE,
  FETCH_APPLIANCE_MEASUREMENTS,
  FETCH_APPLIANCE_EVENTS_AND_LOGS,
  FETCH_APPLIANCE_EVENTS_AND_LOGS_STARTED,
  UPDATE_APPLIANCE_EVENTS
} from '#action/types';
import colors from '#component/shipment/overview/colors.json';
import { whitelabel } from '#config/whitelabel';
import { requestStatuses as statuses } from '#helper';

const defaultState = {
  status: statuses.idle,
  eventsAndLogsStatus: statuses.idle,
  applianceDevices: [],
  allDevices: [],
  logs: [],
  error: null,
  metricsLoadingStatus: {}
};

export default (state = defaultState, action) => {
  switch (action.type) {
    case APPLIANCE_DEVICES_FETCH_STARTED:
      return { ...state, status: statuses.loading, error: null };
    case APPLIANCE_DEVICES_FETCH_SUCCEEDED:
      return {
        ...state,
        status: statuses.succeeded,
        applianceDevices: action.payload
      };
    case ALL_APPLIANCE_DEVICES_FETCH_SUCCEEDED:
      const lineColors = whitelabel.getConfig()?.chart?.line?.color ?? colors;
      const devices = action.payload.reduce((acc, rec) => acc.concat(rec.devices), []);
      return {
        ...state,
        status: statuses.succeeded,
        allDevices: devices.map((d, i) => {
          const color = lineColors[i % lineColors.length];
          return {
            ...state.allDevices.find(stateDevice => stateDevice.id === d.id),
            ...d,
            color,
            alertOn: !!d.projectAlertId,
            showAlertIcon: true
          };
        })
      };
    case ALL_APPLIANCE_DEVICES_SOCKET_UPDATE:
      return {
        ...state,
        allDevices: action.payload
      };
    case FETCH_APPLIANCE_METRICS_BY_IDENTIFIER: {
      const allDevices = state.allDevices.length > 0 ?
        state.allDevices.map(d => {
          const metrics = { ...d?.metrics ?? {} };
          const [allMetrics, ...restMetrics] = action.payload?.filter(m => m.id === d.id) ?? [];
          metrics[action.identifier] = allMetrics?.measurements ?? [];

          if (restMetrics?.length) {
            const metricsChunks = { ...d?.metricsChunks ?? {} };
            metricsChunks[action.identifier] =
              [allMetrics?.measurements, ...restMetrics.map(m => m.measurements)];
            d.metricsChunks = metricsChunks;
          }

          return ({ ...d, metrics });
        }) :
        action.payload?.map(p => ({
          id: p.id,
          metrics: { [action.identifier]: p.measurements ?? [] }
        })) ?? [];

      return { ...state, allDevices };
    }
    case FETCH_APPLIANCE_METRICS_BY_IDENTIFIER_STARTED:
      return {
        ...state,
        metricsLoadingStatus: {
          ...state.metricsLoadingStatus,
          [action.payload]: true
        }
      };
    case FETCH_APPLIANCE_METRICS_BY_IDENTIFIER_COMPLETED:
      return {
        ...state,
        metricsLoadingStatus: {
          ...state.metricsLoadingStatus,
          [action.payload]: false
        }
      };
    case FETCH_APPLIANCE_LOCATIONS: {
      const allDevices = state.allDevices.length > 0 ?
        state.allDevices.map(d => {
          const [allLocations, ...restLocations] =
            action.payload?.filter(m => m.id === d.id) ?? [];

          d.locations = allLocations?.locations ?? d.locations;

          if (restLocations?.length) {
            d.locationsChunks = [allLocations.locations, ...restLocations.map(m => m.locations)];
          }

          return d;
        }) :
        action.payload?.map(d => ({ ...d }));

      return { ...state, allDevices };
    }
    case FETCH_APPLIANCE_MEASUREMENTS:
      return {
        ...state,
        allDevices: state.allDevices.length > 0 ? state.allDevices.map(d => {
          const metrics = { ...d?.metrics ?? {} };
          metrics[action.identifier] = action.payload?.find(m => m.id === d.id)?.measurements ?? [];
          return {
            ...d,
            metrics
          };
        }) : action.payload
      };
    case FETCH_APPLIANCE_EVENTS_AND_LOGS_STARTED:
      return { ...state, eventsAndLogsStatus: statuses.loading };
    case FETCH_APPLIANCE_EVENTS_AND_LOGS: {
      const { events, logs, settingsThresholds } = action.payload;

      const allDevices = state.allDevices.length > 0 ?
        state.allDevices.map(d => {
          const [allEvents, ...restEvents] =
            events?.filter(m => m.id === d.id) ?? [];

          d.events = allEvents?.events ?? d.events;

          if (restEvents?.length) {
            d.eventsChunks = [allEvents.events, ...restEvents.map(m => m.events)];
          }

          return d;
        }) : events;

      return {
        ...state,
        allDevices,
        logs,
        eventsAndLogsStatus: statuses.succeeded,
        settingsThresholds
      };
    }
    case APPLIANCE_DEVICES_FETCH_FAILED:
      return {
        ...state, status: statuses.failed, applianceDevices: [], error: action.error
      };
    case UPDATE_APPLIANCE_EVENTS:
      const { newEvent, acceptance: alertAcceptance } = action.payload;

      const updatedAllDevices = state.allDevices.map(device => ({
        ...device,
        events: device.events.map(event => {
          if (event?.logId === newEvent.details.logId) {
            if (alertAcceptance) {
              return {
                ...event,
                acceptance: {
                  value: alertAcceptance.value
                }
              };
            }

            const { acceptance, ...rest } = event;
            return {
              ...rest
            };
          }
          return event;
        })
      }));

      const updatedLogs = state.logs.map(log => {
        if (log?.logId === newEvent.details.logId) {
          if (alertAcceptance) {
            return {
              ...log,
              acceptance: {
                value: alertAcceptance.value
              }
            };
          }

          const { acceptance, ...rest } = log;
          return {
            ...rest
          };
        }
        return log;
      });

      return {
        ...state,
        allDevices: updatedAllDevices,
        logs: updatedLogs
      };
    case APPLIANCE_DEVICES_CLEAR:
      return defaultState;
    default:
      return state;
  }
};
